import type { KeyboardEvent } from 'react';
import type { UseAppForm } from '../../../form';

import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../../../lib/helpers';

import Button from '../../../../components/Button/Button';

import { ArrowUpIcon, DeleteIcon } from '../../../../images/shapes';

import { useFieldContext, useFormContext } from '../../../form';

export type OptionValue = string | { value: string; label: string };

export type OptionKeyDownFn = (
  e: KeyboardEvent<HTMLInputElement>,
  option: OptionValue,
  currentValue: OptionValue[],
  idx: number,
) => void;

interface OptionFieldProps {
  /**
   * Current option value
   */
  option: OptionValue;
  /**
   * Index of the current option
   */
  idx: number;
  /**
   * Current value of the options field
   */
  currentValue: OptionValue[];
  /**
   * Index of the focused option
   */
  focusIdx?: number;
  /**
   * Function to handle key down events
   */
  onKeyDown: OptionKeyDownFn;
  /**
   * Function to move the option up in the list
   */
  onUp: (idx: number) => void;
  /**
   * Function to move the option down in the list
   */
  onDown: (idx: number) => void;
  /**
   * Function to delete the option
   */
  onDelete: (idx: number) => void;
  /**
   * If field is disabled
   */
  disabled?: boolean;
  /**
   * If field is readonly
   */
  readonly?: boolean;
  /**
   * If options are rendered as key-value pairs
   */
  useOptionsWithLabels?: boolean;
  /**
   * Component test id
   */
  testId?: string;
}

const OptionField = ({
  option,
  idx,
  currentValue,
  focusIdx,
  onKeyDown,
  onUp,
  onDown,
  onDelete,
  disabled,
  readonly,
  useOptionsWithLabels,
  testId,
}: OptionFieldProps) => {
  const { t } = useTranslation();
  const field = useFieldContext<OptionValue>();
  const form = useFormContext() as unknown as UseAppForm;
  const buttonsDisabled = disabled || readonly;

  return (
    <div className="flex flex-row gap-2">
      {useOptionsWithLabels ? (
        <>
          <form.CtoCustomField
            name={`${field.name}.value`}
            properties={{
              inputType: 'text',
              label: '',
            }}
            schema={{
              type: 'string',
            }}
            disabled={disabled}
            readonly={readonly}
            onKeyDown={(e) => onKeyDown(e, option, currentValue, idx)}
            placeholder={t('Global.Value')}
            additionalInputLabelClasses="hidden"
            testId={testId}
            {...(focusIdx === idx && { autoFocus: true })}
          />
          <form.CtoCustomField
            name={`${field.name}.label`}
            properties={{
              inputType: 'text',
              label: '',
            }}
            schema={{
              type: 'string',
            }}
            disabled={disabled}
            readonly={readonly}
            onKeyDown={(e) => onKeyDown(e, option, currentValue, idx)}
            placeholder={t('Global.Label')}
            additionalInputLabelClasses="hidden"
            testId={testId}
            {...(focusIdx === idx && { autoFocus: true })}
          />
        </>
      ) : (
        <form.CtoCustomField
          name={field.name}
          properties={{
            inputType: 'text',
            label: '',
          }}
          schema={{
            type: 'string',
          }}
          disabled={disabled}
          readonly={readonly}
          onKeyDown={(e) => onKeyDown(e, option, currentValue, idx)}
          additionalInputLabelClasses="hidden"
          testId={testId}
          {...(focusIdx === idx && { autoFocus: true })}
        />
      )}

      <div className="flex flex-row gap-1 items-center">
        {currentValue.length > 1 && (
          <>
            <Button
              onClick={() => onUp(idx)}
              iconImage={
                <ArrowUpIcon
                  className={twMerge(
                    'h-3 text-slate-400',
                    idx !== 0 && 'hover:text-slate-700',
                  )}
                />
              }
              buttonColor="borderless"
              disabled={idx === 0 || buttonsDisabled}
              tabIndex={-1}
              additionalClasses="p-1"
              {...getTestProps(testId, `${field.name}-up`, 'testId')}
            />
            <Button
              onClick={() => onDown(idx)}
              iconImage={
                <ArrowUpIcon
                  className={twMerge(
                    'h-3 rotate-180 text-slate-400',
                    idx !== currentValue.length - 1 && 'hover:text-slate-700',
                  )}
                />
              }
              buttonColor="borderless"
              disabled={idx === currentValue.length - 1 || buttonsDisabled}
              tabIndex={-1}
              additionalClasses="p-1"
              {...getTestProps(testId, `${field.name}-down`, 'testId')}
            />
          </>
        )}
        <Button
          onClick={() => onDelete(idx)}
          iconImage={
            <DeleteIcon
              className={twMerge('h-3 text-slate-400 hover:text-slate-700')}
            />
          }
          buttonColor="borderless"
          tabIndex={-1}
          disabled={buttonsDisabled}
          additionalClasses="p-1"
          {...getTestProps(testId, `${field.name}-delete`, 'testId')}
        />
      </div>
    </div>
  );
};

export default OptionField;
